import { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";

import { Webhook, WebhookCreatePayload } from "./Webhook";
import { createWebhook, deleteWebhook, fetchWebhooks, rotateWebhookSecret, updateWebhook } from "./webhooksApi";
import WebhookForm from "./WebhookForm";
import WebhookSecretDialog from "./WebhookSecretDialog";
import WebhookDeliveriesDialog from "./WebhookDeliveriesDialog";
import LoadingSpinner from "../../common/components/loadingSpinner/LoadingSpinner";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | undefined>(undefined);
  const isEditing = selectedWebhook !== undefined;

  const [revealedSecret, setRevealedSecret] = useState<string | undefined>(undefined);
  const [secretDialogTitle, setSecretDialogTitle] = useState<string | undefined>(undefined);

  const [deliveriesWebhook, setDeliveriesWebhook] = useState<Webhook | undefined>(undefined);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    setIsLoading(true);
    const result = await fetchWebhooks();
    setWebhooks(result);
    setIsLoading(false);
  };

  const onCreate = () => {
    setSelectedWebhook(undefined);
    setShowFormDialog(true);
  };

  const onEdit = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setShowFormDialog(true);
  };

  const closeFormDialog = () => {
    setShowFormDialog(false);
    setSelectedWebhook(undefined);
  };

  const onSubmit = async (payload: WebhookCreatePayload) => {
    if (isEditing && selectedWebhook) {
      await updateWebhook({ id: selectedWebhook.id, ...payload });
      closeFormDialog();
      await loadWebhooks();
    } else {
      const created = await createWebhook(payload);
      closeFormDialog();
      setSecretDialogTitle("Webhook creat — salveaza cheia acum");
      setRevealedSecret(created.secret);
      await loadWebhooks();
    }
  };

  const onRotateSecret = (webhook: Webhook) => {
    confirmDialog({
      header: "Confirmare rotatie cheie",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">
          Cheia veche va deveni invalida imediat. Subscriberul trebuie sa stocheze noua cheie inainte de urmatoarea livrare. Continui
          rotatia pentru <span className="font-bold">{webhook.url}</span>?
        </div>
      ),
      acceptLabel: "Da, roteste",
      rejectLabel: "Nu",
      accept: async () => {
        const result = await rotateWebhookSecret(webhook.id);
        setSecretDialogTitle("Cheie regenerata — salveaza noua valoare");
        setRevealedSecret(result.secret);
        await loadWebhooks();
      },
    });
  };

  const onDelete = (webhook: Webhook) => {
    confirmDialog({
      header: "Confirmare stergere",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">
          Esti sigur ca vrei sa stergi webhook-ul <span className="font-bold">{webhook.url}</span>? Livrarile in curs vor fi anulate.
        </div>
      ),
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: async () => {
        await deleteWebhook(webhook.id);
        await loadWebhooks();
      },
    });
  };

  const onCloseSecretDialog = () => {
    setRevealedSecret(undefined);
    setSecretDialogTitle(undefined);
  };

  return (
    <Card
      title={
        <div className="flex align-items-start md:align-items-center justify-content-between">
          <div className="text-2xl font-bold">Webhooks</div>
          <div className="hidden md:flex align-items-center gap-2">
            <Button label="Adauga" icon="pi pi-plus" onClick={onCreate} />
          </div>
        </div>
      }
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <DataTable size="small" value={webhooks} emptyMessage={<NoResultsFound />}>
          <Column
            className="w-5rem"
            field="isEnabled"
            body={(rowData: Webhook) => (rowData.isEnabled ? <Tag severity="success">Activ</Tag> : <Tag severity="danger">Inactiv</Tag>)}
          />
          <Column
            field="url"
            header="URL"
            body={(rowData: Webhook) => (
              <div className="flex flex-column">
                <span className="font-mono">{rowData.url}</span>
                {rowData.description && <span className="text-color-secondary text-sm">{rowData.description}</span>}
              </div>
            )}
          />
          <Column
            className="w-22rem"
            body={(rowData: Webhook) => (
              <div className="flex align-items-center justify-content-end gap-2">
                <Button
                  className="hidden md:flex"
                  size="small"
                  label="Livrari"
                  severity="secondary"
                  icon="pi pi-list"
                  onClick={() => setDeliveriesWebhook(rowData)}
                />
                <Button size="small" label="Editeaza" icon="pi pi-pencil" onClick={() => onEdit(rowData)} />
                <Button size="small" label="Sterge" severity="danger" icon="pi pi-trash" onClick={() => onDelete(rowData)} />
              </div>
            )}
          />
        </DataTable>
      )}

      <Dialog
        className="w-almost-full md:w-6"
        header={isEditing ? "Editeaza webhook" : "Adauga webhook"}
        visible={showFormDialog}
        onHide={closeFormDialog}
      >
        <WebhookForm
          webhook={selectedWebhook}
          onSubmit={onSubmit}
          onRotateSecret={selectedWebhook ? () => onRotateSecret(selectedWebhook) : undefined}
        />
      </Dialog>

      <WebhookSecretDialog
        visible={revealedSecret !== undefined}
        secret={revealedSecret ?? ""}
        title={secretDialogTitle}
        onClose={onCloseSecretDialog}
      />

      <WebhookDeliveriesDialog
        webhook={deliveriesWebhook}
        visible={deliveriesWebhook !== undefined}
        onClose={() => setDeliveriesWebhook(undefined)}
      />
    </Card>
  );
}
